// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AppEvent } from '@interfaces/interfaces';
import { MatDialog } from '@angular/material/dialog';
import { SystemDocumentModalComponent } from 'src/app/modals/system-document-modal/system-document-modal.component';
import { SystemDocument, SystemDocumentType } from '@interfaces/document.interfaces';
import { SystemDocumentTypes, UserSignatureCall } from '@interfaces/user.interfaces';
import { UserService } from '@services/user.service';
import { AuthService } from 'src/app/login/services/auth.service';
import { SharedService } from '@services/shared.service';
import { ModalSize } from '@interfaces/shared.interfaces';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'accept-terms',
    templateUrl: './accept-terms.component.html',
    styleUrls: ['./accept-terms.component.scss'],
})
export class AcceptTermsComponent implements OnInit, OnChanges, AfterViewInit {
    @ViewChild('content') content: ElementRef;
    @Input() requiredDocs: any[] = [];
    @Input() showButtons = true;
    @Input() showTitle = true;
    @Input() initiallyChecked = false;
    @Output() outEvent: EventEmitter<AppEvent> = new EventEmitter();
    protected readonly SystemDocumentType = SystemDocumentType;
    public accepted: any[] = [];
    public showCloseButton = false;
    public newSystemDocs: SystemDocument[] = [];
    public newReleaseNotes: SystemDocument[] = [];
    public moreAgreements = false;
    public title = 'Go Back';
    private hasSignatures = false;
    private hasPendingSystemDocs = true;
    public isDirectLogin: boolean;
    private firstTimeLoginSetup = false;

    constructor(
        private readonly dialog: MatDialog,
        private readonly userService: UserService,
        private readonly authService: AuthService,
        private readonly sharedService: SharedService
    ) {
        if (
            !window.location.pathname.includes('welcome') &&
            !window.location.pathname.includes('sso') &&
            !window.location.pathname.includes('firstUserLogging')
        ) {
            this.showCloseButton = true;
            // button should say log out if they are not in the new user (or new sso user) process
            this.title = 'Log Out';
        }
    }

    get allDocsChecked(): boolean {
        for (const doc of this.newSystemDocs) {
            if (!doc.accepted) {
                return false;
            }
        }
        return true;
    }

    async ngOnInit(): Promise<void> {
        if (window.location.href.includes('/firstUserLogging')) {
            this.isDirectLogin = true;
        } else {
            this.isDirectLogin = false;
        }

        this.firstTimeLoginSetup = false;
        const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated());

        if (isAuthenticated) {
            this.hasPendingSystemDocs = this.userService.userNeedsToAcceptSystemDocuments();
            this.userService.getUserSignature$(UserSignatureCall.signature).subscribe((x) => {
                this.hasSignatures = x.length > 0;

                if (!this.hasSignatures && this.hasPendingSystemDocs) {
                    this.firstTimeLoginSetup = true;
                }
            });
        }

        this.setSystemDocsAndReleaseNotes();

        // throw new Error("Method not implemented.");
        this.outEvent.emit({
            context: 'loading-start',
            data: {},
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.requiredDocs?.currentValue) {
            this.requiredDocs = changes.requiredDocs.currentValue;
            this.setSystemDocsAndReleaseNotes();
        }
    }

    ngAfterViewInit(): void {
        if (this.newReleaseNotes.length) {
            this.displayReleaseNotes(this.newReleaseNotes[0]);
        }

        this.outEvent.emit({
            context: 'loading-done',
            data: {},
        });
    }

    public continue(): void {
        this.userService.setUser({ ...this.userService.user, needToAcceptSystemDocument: false }); //defensive protection against race condition
        this.outEvent.emit({
            context: 'step-2-complete',
            data: this.requiredDocs || [],
        });
    }

    private setSystemDocsAndReleaseNotes(): void {
        this.newSystemDocs = this.requiredDocs.filter((doc) => doc.systemDocumentTypeId !== SystemDocumentTypes.releaseNotes);
        this.newReleaseNotes = this.requiredDocs.filter((doc) => doc.systemDocumentTypeId === SystemDocumentTypes.releaseNotes);
    }

    private displayReleaseNotes(releaseNotes: SystemDocument): void {
        const appended = document.getElementsByClassName('release-doc-container');

        if (appended.length > 0) {
            const parser = new DOMParser();
            const html = releaseNotes.htmlDoc;
            const docHTML = parser.parseFromString(html, 'text/html');
            appended[0]?.appendChild(docHTML.body);
            const a = appended[0]?.firstChild as HTMLElement;
            a.style.padding = '1rem';
        }
    }

    public firstStep(): void {
        this.moreAgreements = false;
    }

    public goBack(): void {
        // if button says go back take them to previous step, they are in new user process
        if (this.title.includes('Go')) {
            if (!window.location.pathname.includes('sso')) {
                this.outEvent.emit({
                    context: 'step-2-go-back',
                    data: {},
                });
            } else {
                this.outEvent.emit({
                    context: 'goBackToDpa',
                    data: {},
                });
            }
        }
        // button should log out if they are not in the new user process
        else {
            this.logout();
        }
    }

    public toggleIsChecked(document: SystemDocument, { checked }): void {
        document.accepted = checked;
        this.outEvent.emit({ context: 'onChange', data: { ...document } });
    }

    public logout(): void {
        this.userService.logOut();
    }

    public nextStep(): void {
        if (this.newReleaseNotes.length > 0) {
            this.moreAgreements = true;
        } else {
            this.continue();
        }
    }

    public systemDocumentDialog(docType: string) {
        this.dialog.open(SystemDocumentModalComponent, {
            width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
            height: this.sharedService.getModalHeight(ModalSize.Medium) + 'px',
            closeOnNavigation: false,
            data: {
                modalSize: ModalSize.Medium,
                doc: this.newSystemDocs.filter((doc) => doc.systemDocumentDescription === docType)[0],
            },
        });
    }

    public firstTimeLoginToAcceptSystemDocs() {
        return this.firstTimeLoginSetup;
    }
}
